import type { Axios } from "axios";
import { function as function_, taskEither } from "fp-ts";

import { AuthServicePorts } from "../../model";

import { URLS } from "./consts";
import { ChangePassword, GetMe, Login, Logout, Register, ResetPassword } from "./ios";
import { mapDtoToEntity } from "./mappers";

import { UserEntity } from "~/entities/user/@x/auth";
import { Response } from "~/shared/api";
import {
  AuthenticationError,
  AuthorizationError,
  NotFoundError,
  UnexpectedError,
  UniqueKeyViolationError,
} from "~/shared/error";

class ApiProvider extends AuthServicePorts.Provider {
  private readonly axios: Axios;

  constructor(axios: Axios) {
    super();
    this.axios = axios;
  }

  override register(
    params: AuthServicePorts.ProviderIos.Register.In,
  ): taskEither.TaskEither<
    UnexpectedError | UniqueKeyViolationError<UserEntity.Constraint>,
    AuthServicePorts.ProviderIos.Common.Out
  > {
    return function_.pipe(
      Response.process(() => this.axios.post(URLS.register, params), Register.zRes),
      taskEither.flatMap((response) => {
        const { status, body } = response;
        if (status === 200) return taskEither.right(mapDtoToEntity(body.data));

        if (status === 400 && body.errorDetails.codeErrors.includes("EMAIL_ALREADY_EXISTS"))
          return taskEither.left(
            new UniqueKeyViolationError(UserEntity.Constraint.UNIQUE_USER_EMAIL),
          );

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override login({
    rememberMe,
    ...params
  }: AuthServicePorts.ProviderIos.Login.In): taskEither.TaskEither<
    UnexpectedError | NotFoundError,
    AuthServicePorts.ProviderIos.Common.Out
  > {
    return function_.pipe(
      Response.process(
        () => this.axios.post(URLS.login, { ...params, isRememberMe: rememberMe }),
        Login.zRes,
      ),
      taskEither.flatMap((response) => {
        const { status, body } = response;
        if (status === 200) return taskEither.right(mapDtoToEntity(body.data));

        if (status === 401) return taskEither.left(new NotFoundError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override logout(): taskEither.TaskEither<UnexpectedError | AuthenticationError, void> {
    return function_.pipe(
      Response.process(() => this.axios.post(URLS.logout), Logout.zRes),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 200) return taskEither.right(void 0);

        if (status === 401) return taskEither.left(new AuthenticationError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override changePassword({
    id,
    ...params
  }: AuthServicePorts.ProviderIos.ChangePassword.In): taskEither.TaskEither<
    | UnexpectedError
    | AuthenticationError
    | AuthorizationError
    | NotFoundError
    | AuthServicePorts.ProviderIos.ChangePassword.Error,
    void
  > {
    return function_.pipe(
      Response.process(
        () =>
          this.axios.post(URLS.changePassword, {
            ...params,
            userId: id,
          }),
        ChangePassword.zRes,
      ),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 200) return taskEither.right(void 0);

        if (status === 400) {
          const { codeErrors } = response.body.errorDetails;
          if (codeErrors.includes("USER_NOT_FOUND")) return taskEither.left(new NotFoundError());

          if (codeErrors.includes("PasswordMismatch"))
            return taskEither.left(
              new AuthServicePorts.ProviderIos.ChangePassword.Error(
                AuthServicePorts.ProviderIos.ChangePassword.ErrorReason.CURRENT_PASSWORD_IS_INCORRECT,
              ),
            );
        }

        if (status === 401) return taskEither.left(new AuthenticationError());

        if (status === 403) return taskEither.left(new AuthorizationError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override resetPassword(
    params: AuthServicePorts.ProviderIos.ResetPassword.In,
  ): taskEither.TaskEither<UnexpectedError | NotFoundError, void> {
    return function_.pipe(
      Response.process(() => this.axios.post(URLS.resetPassword, params), ResetPassword.zRes),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 200) return taskEither.right(void 0);

        if (status === 400 && response.body.errorDetails.codeErrors.includes("USER_NOT_FOUND"))
          return taskEither.left(new NotFoundError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override getMe(): taskEither.TaskEither<
    UnexpectedError | AuthenticationError,
    AuthServicePorts.ProviderIos.Common.Out
  > {
    return function_.pipe(
      Response.process(() => this.axios.get(URLS.getMe), GetMe.zRes),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 200) return taskEither.right(mapDtoToEntity(response.body.data));

        if (status === 401) return taskEither.left(new AuthenticationError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }
}

export { ApiProvider };
